<script setup>
import axios from 'axios';
import {Modal} from "bootstrap";
import StepperReserv from "./StepperReserv.vue";
import GalleryReserv from "./GalleryReserv.vue";
import {computed, onMounted, ref, watch, onUnmounted} from "vue";
import {useConfigurateurStore} from "../../stores/configurateur";
import {useGlobalLoaderStore} from "../../stores/globalLoader";
import StepCreneauReserv from "./StepCreneauReserv.vue";
import StepVoitureReserv from "./StepVoitureReserv.vue";
import StepCircuitReserv from "./StepCircuitReserv.vue";
import StepTourReserv from "./StepTourReserv.vue";
import StepOptionReserv from "./StepOptionReserv.vue";
import StepComplementReserv from "./StepComplementReserv.vue";
import ConfigurateurHelpers from "../../utils/configurateurHelpers";
import Logger from "../../utils/logger";

const logger = new Logger('configurateur')

const store = useConfigurateurStore();
const globalLoaderStore = useGlobalLoaderStore();

// Détection mobile pour éviter de charger les images de la gallery
const isMobile = ref(false);

// Garde contre les double-clics pendant l'ajout au panier
const isSubmitting = ref(false);

const checkScreenSize = () => {
    isMobile.value = window.innerWidth < 992; // Bootstrap lg breakpoint
};
const steps = computed(() => {
    // Mapper les noms d'étapes aux composants
    const stepComponents = {
        'voiture': StepVoitureReserv,
        'circuit': StepCircuitReserv,
        'tour': StepTourReserv,
        'creneau': StepCreneauReserv,
        'option': StepOptionReserv,
        'complement': StepComplementReserv
    };

    // Utiliser le getter du store qui gère le masquage selon l'expérience
    return store.steps.map(stepName => stepComponents[stepName]).filter(Boolean);
})

const nbExperiences = computed(() => {
    // En mode baptême pur : seulement 1 expérience (le baptême)
    if (store.experience === 'bapteme') {
        return 1;
    }

    // En mode pilotage : 1 expérience par défaut (stage principal)
    let count = 1;

    // +1 si baptême passager ajouté en complément
    if (store.nb_tours_bapteme > 0) {
        count++;
    }

    // +x en fonction du nombre de voitures complémentaires (étape 6)
    // Exclure le baptême qui est déjà compté ci-dessus
    if (store.stage_ids && store.stage_ids.length > 0) {
        let complementaireCount = store.stage_ids.length;

        // Si il y a un baptême dans stage_ids, on le soustrait pour éviter double comptage
        if (store.nb_tours_bapteme > 0 && store.stageBaptemeInStageIds) {
            complementaireCount--;
        }

        count += complementaireCount;
    }

    return count;
})

onMounted(() => {
    store.setModal(new Modal('#modal-config-reservation', {}))
    document.querySelector('#modal-config-reservation').addEventListener('hidden.bs.modal', () => {
        store.setStep(store.first_step)
    })
    let hasHashConfigurateur = window.location.hash.includes('configurateur');
    if (hasHashConfigurateur) {
        let parts = window.location.hash.split('-');
        let type = parts[1] || null;
        const helper = new ConfigurateurHelpers({
            el: null, // Pas d'élément DOM spécifique dans ce contexte
            store: store,
            storeGlobalLoader: globalLoaderStore
        });
        if (type == 'stage') {
            store.setExperience('pilotage')
            // store.setStep('voiture')
            // store.openModal()
            helper.processConfigurateur()
        } else if (type == 'bapteme') {
            store.setExperience('bapteme')
            // store.setStep('voiture')
            // store.openModal()
            helper.processConfigurateur()
        } else {
            // Affiche showExperienceChoicePopup via le helper

            helper.showExperienceChoicePopup();
        }
    }

    // Initialiser la détection mobile
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize);
});

const handleClickNextStep = () => {
    document.querySelector('body').dispatchEvent(new CustomEvent('next-step'))
}

const handleClickPrevStep = () => {
    document.querySelector('body').dispatchEvent(new CustomEvent('prev-step'))
}

const handleClickSubmit = async () => {
    if (isSubmitting.value) {
        return;
    }

    // Vérifier la validation avant de procéder
    if (!store.canValidateConfiguration) {
        // Afficher une modale d'erreur avec le message récapitulatif
        const errorMessage = store.validationErrorMessage || "Veuillez compléter tous les champs obligatoires.";

        // Créer et afficher une modale d'erreur
        const modalHtml = `
            <div class="modal fade" id="validation-error-modal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                <i class="bi bi-exclamation-triangle text-warning me-2"></i>
                                Configuration incomplète
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-0">${errorMessage}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
                                Compris
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Ajouter la modale au DOM
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Afficher la modale
        const errorModal = new Modal(document.getElementById('validation-error-modal'));
        errorModal.show();

        // Nettoyer la modale après fermeture
        document.getElementById('validation-error-modal').addEventListener('hidden.bs.modal', () => {
            document.getElementById('validation-error-modal').remove();
        });

        return;
    }

    logger.log('🚀 DEBUT handleClickSubmit - DEBUG MODE BAPTEME');
    logger.log('🔍 Variables du store:', {
        experience: store.experience,
        nb_tours: store.nb_tours,
        nb_tours_bapteme: store.nb_tours_bapteme,
        stage_ids_length: store.stage_ids.length,
        stage_ids: store.stage_ids
    });

    isSubmitting.value = true;

    try {
        // Initialiser la session complètement en faisant d'abord un appel à une route web
        await axios.get('/cart/count');
        await axios.get('/sanctum/csrf-cookie');

        // Choisir le bon stage selon le contexte :
        // - Mode baptême avec tours baptême : utiliser stageBapteme  
        // - Mode pilotage : utiliser stage (pilotage)
        const targetStage = (store.experience === 'bapteme' && store.nb_tours_bapteme > 0)
            ? store.stageBapteme
            : store.stage;

        logger.log('🎯 Target stage sélectionné:', {
            condition: store.experience === 'bapteme' && store.nb_tours_bapteme > 0,
            targetStage: targetStage,
            stage: store.stage,
            stageBapteme: store.stageBapteme
        });

        let params = {
            product_id: targetStage.product_id,
            stage_id: targetStage.stage_id,
            circuit_id: store.circuit_id,
            voiture_id: store.voiture_id,
            nb_tours: store.experience === 'bapteme' && store.nb_tours_bapteme > 0 ? store.nb_tours_bapteme : store.nb_tours,
            quantity: store.experience === 'bapteme' && store.nb_tours_bapteme > 0 ? store.nb_tours_bapteme : store.nb_tours,
            options: store.options,
            stage_ids: store.stage_ids,
            creneau_human: store.creneau_human,
            circuit_date_id: store.circuit_date_id,
            circuit_date_creneau_id: store.circuit_date_creneau_id,
            circuit_date_creneau_voiture_id: store.circuit_date_creneau_voiture_id,
            date: store.date,
            creneau: store.creneau,
            possibilite_id: store.possibilite ? store.possibilite.id : null,
        };

        // En mode baptême avec tours baptême, on a déjà le bon stage dans targetStage
        if (store.experience === 'bapteme' && store.nb_tours_bapteme > 0) {
            // Mode baptême : on utilise le stage baptême comme stage principal
            // Ne rien faire, les params sont déjà corrects
        } else if (!store.stageValidToCart && store.stageBaptemeInStageIds) {
            // Mode pilotage avec baptême : supprimer le stage pilotage s'il n'est pas valide
            params.stage_id = null;
            params.product_id = null;
        } else if (!store.stageValidToCart && !store.stageBaptemeInStageIds) {
            // Aucun stage valide
            alert('merci de choisir un stage ou bapteme pour continuer');
            isSubmitting.value = false;
            return;
        }

        // Retry logic en cas d'échec du premier appel
        let response;
        let attempts = 0;
        const maxAttempts = 2;

        while (attempts < maxAttempts) {
            try {
                response = await axios.post('/api/checkout/cart', params);
                break; // Succès, sortir de la boucle
            } catch (error) {
                attempts++;
                if (error.response?.status === 404 && attempts < maxAttempts) {
                    logger.warn(`Tentative ${attempts} échouée (404), retry...`);
                    // Attendre un peu et réessayer
                    await new Promise(resolve => setTimeout(resolve, 500));
                    // Re-initialiser la session
                    await axios.get('/cart/count');
                    await axios.get('/sanctum/csrf-cookie');
                } else {
                    throw error; // Relancer l'erreur si ce n'est pas une 404 ou si on a épuisé les tentatives
                }
            }
        }

        store.closeModal(); // Close the modal before redirecting
        store.cleanConfigurateur()
        window.location.href = '/checkout/cart';
    } catch (error) {
        console.error('Erreur lors de l\'ajout au panier:', error);
        alert('Une erreur est survenue lors de l\'ajout au panier. Veuillez réessayer.');
        isSubmitting.value = false;
    }
}

// Watcher pour arrêter le GlobalLoader quand le chargement de la modal est terminé
watch(() => store.modalLoader, (newValue) => {
    if (newValue === false) {
        globalLoaderStore.stop();
    }
});


</script>

<template>
    <div id="modal-config-reservation" class="modal modal-config-reserv fade" tabindex="-1"
         aria-labelledby="modal-config-reservation" aria-hidden="true">
        <div class="modal-dialog modal-fullscreen modal-dialog-scrollable">
            <!-- Utiliser v-if="store.step" ici pour éviter d'afficher le contenu avant que le step initial soit défini -->
            <div class="modal-content" v-if="store.step">
                <div class="modal-header">
                    <p class="modal-title">
                        {{
                            store.experience === 'bapteme' ? $t('Créer votre baptême passager') : $t('Créer votre stage de pilotage')
                        }}
                    </p>
                    <button type="button" class="btn-close" @click="store.closeModal()" aria-label="Close"></button>
                </div>

                <!-- Indicateur de chargement global pour l'ouverture initiale -->
                <div v-if="store.modalLoader" class="modal-body h-100 d-flex justify-content-center align-items-center">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <!-- Contenu principal une fois le chargement initial terminé -->
                <div class="modal-body h-100" v-else>
                    <div class="row h-100">
                        <div class="col-lg-8 col-md-12 h-100 d-none d-lg-block">
                            <!-- Stepper -->
                            <StepperReserv/>

                            <!-- Carousel -->
                            <GalleryReserv v-if="!isMobile"/>
                        </div>

                        <!-- Contenu dynamique (colonne de droite) -->
                        <div class="right-column col-lg-4 col-md-12 h-100 pt-4 pt-lg-0 p-0">
                            <!-- Ajout de la classe conditionnelle ici -->
                            <div class="scrollspy" :class="{ 'is-loading': store.isLoadingStep }">
                                <form class="form" action="" method="post">
                                    <component :is="step" v-for="step in steps" :key="step"/>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Validation -->

                <div class="modal-footer" v-if="!store.modalLoader">
                    <div class="row m-0 w-100">
                        <div class="steps col-lg-8 col-md-12 d-block d-md-flex m-0">
                            <ul class="row row-cols-2 row-cols-md-3 list-unstyled m-0 py-3 px-0">
                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.voiture_id, 'text-white': store.voiture_id}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    <template v-if="store.voiture">{{ store.voiture.marque }}
                                        {{ store.voiture.designation }}
                                    </template>
                                </li>

                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.creneau_human, 'text-white': store.creneau_human}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    {{ $t('Date') }} & {{ $t('Horaire') }}
                                    <template v-if="store.creneau_human">
                                        : {{ store.creneau_human }}
                                    </template>
                                </li>

                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.options.length, 'text-white': store.options.length}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    {{ $t('Option') }}
                                </li>


                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.circuit_id || !store.isCompleted('circuit'), 'text-white': store.circuit_id}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    <template v-if="!store.circuit">{{ $t('Circuit') }}</template>
                                    <template v-else>{{ store.circuit.designation }}</template>
                                </li>

                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.nb_tours || !store.isCompleted('tour'), 'text-white': store.nb_tours}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    <template v-if="!store.nb_tours">{{ $t('Nombre de tour') }}</template>
                                    <template v-else>{{ $t('Nombre de tour') }} : {{ store.nb_tours }}</template>
                                </li>

                                <li class="col d-flex align-items-center"
                                    :class="{'text-white-50': !store.stage_ids.length, 'text-white': store.stage_ids.length}">
                                    <i class="bi bi-check-circle me-2"></i>
                                    {{ $t('Voiture complémentaire') }}
                                </li>
                            </ul>
                        </div>

                        <div class="validation col-lg-4 col-md-12 m-0">
                            <div class="row d-flex bg-white rounded p-3 ms-lg-2">
                                <div class="col-5 p-0">
                                    <!--<p class="fs-6 fw-normal text-gray-60 m-0 small" v-if="store.price.perTours">1 tour
                                        =
                                        {{ store.voiture.price.montant_ttc_par_tour }}€</p>-->
                                    <p class="fs-6 fw-bolder m-0 small">
                                        {{ nbExperiences }} {{
                                            nbExperiences > 1 ? $t('expériences') : $t('expérience')
                                        }}
                                    </p>
                                    <div class="price">
                                        <div class="d-flex align-items-baseline">
                                            <span
                                                v-if="store.price.oldTotal && store.price.oldTotal > store.price.total"
                                                class="old-price fw-normal text-decoration-line-through me-2 text-muted">
                                                {{ store.price.oldTotal }}€
                                            </span>
                                            <p class="price fw-bolder text-info fs-lg-5 fs-xl-4 m-0">{{
                                                    store.price.total
                                                }}€</p>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-7 p-0 align-content-end">
                                    <!-- Bouton Valider (dernière étape) -->
                                    <div v-if="store.isLastStep" class="d-grid gap-2">
                                        <button @click.prevent="handleClickSubmit"
                                                :disabled="isSubmitting"
                                                class="btn btn-primary with-box-shadow fw-bold fs-6 mb-1" type="submit">
                                            <span v-if="isSubmitting"
                                                  class="spinner-border spinner-border-sm me-2"
                                                  role="status"
                                                  aria-hidden="true"></span>
                                            {{ isSubmitting ? $t('Ajout au panier...') : $t('Valider') }}
                                        </button>
                                        <button v-if="store.step != store.steps[0]"
                                                @click.prevent="handleClickPrevStep"
                                                :disabled="isSubmitting"
                                                class="btn btn-outline-light fw-bold fs-6">
                                            {{ $t('Retour') }}
                                        </button>
                                    </div>

                                    <!-- Boutons Navigation (étapes intermédiaires) -->
                                    <div v-else class="d-grid gap-2">
                                        <button @click.prevent="handleClickNextStep"
                                                class="btn btn-primary with-box-shadow fw-bold fs-6 flex-fill">
                                            {{ $t('Etape suivante') }}
                                        </button>
                                        <button v-if="store.step != store.steps[0]"
                                                @click.prevent="handleClickPrevStep"
                                                class="btn btn-outline-light fw-bold fs-6 flex-fill">
                                            {{ $t('Retour') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Fallback si store.step n'est pas encore défini (optionnel) -->
            <div class="modal-content" v-else>
                <div class="modal-body h-100 d-flex justify-content-center align-items-center">
                    <!-- Peut-être un spinner ou un message -->
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
/* Style pour la colonne pendant le chargement */
.scrollspy.is-loading form {
    position: relative;
    min-height: 100px;
    pointer-events: none; /* Désactive les interactions sur toute la colonne */
}

/* Overlay de chargement (fond semi-transparent) */
.scrollspy.is-loading form::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 100%;
    height: auto;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 50;
}

/* Container blanc avec spinner intégré */
.scrollspy.is-loading form::after {
    content: '';
    position: fixed;
    top: 50%;
    right: 15%;
    transform: translate(-50%, -50%);


    /* Taille et style du container blanc (comme GlobalLoader) */
    width: 90px;
    height: 90px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    z-index: 51;

    /* Créer le spinner exactement comme dans GlobalLoader */
    border: 5px solid #f3f3f3;
    border-top: 5px solid #3498db;
    border-radius: 50%;

    /* Animation du spinner seulement (pas du container) */
    animation: spinner-only 1s linear infinite;
}


@media (max-width: 992px) {
    .scrollspy.is-loading form::after {
        right: 50%;
    }
}

/* Animation qui fait tourner seulement le spinner, pas le container */
@keyframes spinner-only {
    0% {
        transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
        transform: translate(-50%, -50%) rotate(360deg);
    }
}
</style>
